import { useEffect, useState } from 'react';
import { isDemoMode } from '../../config/env';
import { dancerEmoji, type Honorific, type PersonVisual } from '../../lib/honorific';
import { api } from '../../services/api';
import { DEMO_USERS } from '../../demo/demoData';
import './uiAtoms.css';

// Module-level caches to prevent redundant fetching, avoid flashing, and persist object URLs
const avatarBlobCache = new Map<string, string | null>();
const inFlightAvatarRequests = new Map<string, Promise<string | null>>();

export function setCachedAvatar(userId: string, url: string | null) {
  avatarBlobCache.set(userId, url);
}

export function normalizePhotoUrl(url?: string | null): string | null {
  if (!url) return null;
  // If it's an SVG data URL with raw/unencoded XML, encode it safely for <img> src
  if (url.startsWith('data:image/svg+xml') && url.includes('<svg')) {
    const commaIndex = url.indexOf(',');
    if (commaIndex !== -1) {
      const body = url.slice(commaIndex + 1);
      return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(body)}`;
    }
  }
  return url;
}

function getDemoAvatar(userId?: string, name?: string): { photoUrl: string | null; honorific?: Honorific } {
  try {
    const raw = sessionStorage.getItem('dancemance_demo_state');
    if (raw) {
      const parsed = JSON.parse(raw);
      const user = parsed?.people?.find(
        (p: any) => (userId && (p._id === userId || p.id === userId)) || (name && p.name === name),
      );
      if (user?.photoUrl) {
        return { photoUrl: normalizePhotoUrl(user.photoUrl), honorific: user.honorific };
      }
      if (user) {
        return { photoUrl: null, honorific: user.honorific };
      }
    }
  } catch {}

  const fallback = DEMO_USERS.find(
    (p) => (userId && (p._id === userId || (p as any).id === userId)) || (name && p.name === name),
  );
  if (fallback?.photoUrl) {
    return { photoUrl: normalizePhotoUrl(fallback.photoUrl), honorific: fallback.honorific };
  }
  return { photoUrl: null, honorific: fallback?.honorific };
}

export function PersonAvatar({
  name,
  honorific,
  photoFileId,
  photoUrl,
  userId,
  color = '#8B6F47',
  size = 'md',
}: {
  name: string;
  honorific?: Honorific | null;
  photoFileId?: string | null;
  photoUrl?: string | null;
  userId?: string;
  color?: string;
  size?: 'sm' | 'md' | 'lg';
}) {
  const cacheKey = userId || (name ? `name:${name}` : '');
  const [resolvedHonorific, setResolvedHonorific] = useState<Honorific | undefined>(honorific ?? undefined);

  const [src, setSrc] = useState<string | null>(() => {
    if (photoUrl) return normalizePhotoUrl(photoUrl);
    if (cacheKey && avatarBlobCache.has(cacheKey)) {
      return avatarBlobCache.get(cacheKey) ?? null;
    }
    if (isDemoMode) {
      const demo = getDemoAvatar(userId, name);
      if (demo.photoUrl) return demo.photoUrl;
    }
    return null;
  });

  useEffect(() => {
    if (honorific) {
      setResolvedHonorific(honorific);
    }
  }, [honorific]);

  useEffect(() => {
    if (photoUrl) {
      const normalized = normalizePhotoUrl(photoUrl);
      setSrc(normalized);
      if (cacheKey) avatarBlobCache.set(cacheKey, normalized);
      return;
    }

    if (cacheKey && avatarBlobCache.has(cacheKey)) {
      setSrc(avatarBlobCache.get(cacheKey) ?? null);
      return;
    }

    if (isDemoMode) {
      const demo = getDemoAvatar(userId, name);
      if (demo.photoUrl) {
        if (cacheKey) avatarBlobCache.set(cacheKey, demo.photoUrl);
        setSrc(demo.photoUrl);
      } else {
        setSrc(null);
      }
      if (!honorific && demo.honorific) {
        setResolvedHonorific(demo.honorific);
      }
      return;
    }

    if (!userId) {
      setSrc(null);
      return;
    }

    // In live mode: if photoFileId is explicitly null, don't query
    if (photoFileId === null) {
      avatarBlobCache.set(userId, null);
      setSrc(null);
      return;
    }

    let alive = true;
    let req = inFlightAvatarRequests.get(userId);
    if (!req) {
      req = api
        .getBlob(`/users/${userId}/photo`)
        .then((blobUrl) => {
          avatarBlobCache.set(userId, blobUrl);
          return blobUrl;
        })
        .catch(() => {
          avatarBlobCache.set(userId, null);
          return null;
        })
        .finally(() => {
          inFlightAvatarRequests.delete(userId);
        });
      inFlightAvatarRequests.set(userId, req);
    }

    req.then((url) => {
      if (alive) {
        setSrc(url);
      }
    });

    return () => {
      alive = false;
    };
  }, [photoUrl, photoFileId, userId, name, cacheKey, honorific]);

  const emoji = dancerEmoji(resolvedHonorific);

  return (
    <span
      className={`ui-avatar ui-avatar--${size} ${src ? 'ui-avatar--photo' : ''} ${src ? '' : 'ui-avatar--emoji'}`}
      style={src ? undefined : { background: `${color}18`, color }}
      aria-label={name}
    >
      {src ? (
        <img
          src={src}
          alt={name}
          onError={() => {
            setSrc(null);
            if (cacheKey) avatarBlobCache.set(cacheKey, null);
          }}
        />
      ) : (
        emoji
      )}
    </span>
  );
}

export function PersonStack({ people, color }: { people: PersonVisual[]; color?: string }) {
  const shown = people.slice(0, 3);
  if (shown.length === 0) return null;
  return (
    <span className="person-stack" aria-hidden="true">
      {shown.map((person, index) => {
        const uid = person._id ?? person.id ?? person.userId;
        return (
          <PersonAvatar
            key={uid ?? `${person.name}-${index}`}
            name={person.name}
            honorific={person.honorific}
            photoFileId={person.photoFileId}
            photoUrl={person.photoUrl}
            userId={uid}
            color={color}
            size="sm"
          />
        );
      })}
    </span>
  );
}
